<template>
  <section class="custom-access-request-detail">
    <div class="row custom-page-header align-items-center">
      <div class="col-auto">
        <button type="button" class="btn btn-light" @click="$emit('closing')"><i class="fa fa-arrow-left me-1"></i>Back to List</button>
      </div>
      <div v-if="request" class="col">
        <h4 class="mb-0">
          {{ request.firstName }} {{ request.lastName }}
          <span v-if="request.companyName" class="text-muted">&middot; {{ request.companyName }}</span>
          <span class="badge ms-2 align-middle" :class="status.badge" data-test="request-status">{{ status.name }}</span>
        </h4>
      </div>
    </div>

    <div v-if="loading.request && !request" class="custom-spinner"><FontAwesomeIcon icon="fa-solid fa-spinner" spin /></div>
    <div v-if="error" class="alert alert-danger mt-3" role="alert">{{ error }}</div>
    <div v-if="notice" class="alert alert-success mt-3" role="status">{{ notice }}</div>

    <div v-if="request" class="row mt-3 g-3">
      <!-- What the requester sent -->
      <div class="col-lg-4">
        <div class="card h-100">
          <div class="card-header">Request</div>
          <div class="card-body">
            <dl class="custom-fields mb-0">
              <dt>Email</dt>
              <dd>{{ request.email }}</dd>
              <dt>Phone</dt>
              <dd>{{ request.phone || '-' }}</dd>
              <dt>Company</dt>
              <dd>{{ request.companyName || '-' }}</dd>
              <dt>ADAS Map username</dt>
              <dd>{{ request.requestedUsername || 'Not given' }}</dd>
              <dt>Message</dt>
              <dd class="custom-pre">{{ request.message || '-' }}</dd>
              <dt>Submitted</dt>
              <dd>
                {{ formatDate(request.createdOn) }}
                <span v-if="request.submitCount > 1" class="text-muted"
                  >, sent {{ request.submitCount }} times (last {{ formatDate(request.lastSubmittedOn) }})</span
                >
              </dd>
              <template v-if="request.decidedOn">
                <dt>Decided</dt>
                <dd>{{ formatDate(request.decidedOn) }} by {{ request.decidedBy }}</dd>
              </template>
            </dl>
          </div>
        </div>
      </div>

      <!-- Who it is in Launchpad -->
      <div class="col-lg-8">
        <div class="card h-100">
          <div class="card-header">Launchpad user</div>
          <div class="card-body">
            <template v-if="request.linkedUser">
              <user-summary :user="request.linkedUser" />
              <button v-if="canEditLink" type="button" class="btn btn-sm btn-outline-secondary mt-2" :disabled="loading.saving" @click="link(0)">
                Unlink
              </button>
              <p v-else-if="isApproved" class="text-muted small mt-2 mb-0">Revoke access before linking a different user.</p>
            </template>
            <template v-else>
              <p class="mb-2">
                Not linked yet. Approving turns on access for the linked user, so check you have the right person. If they have no Launchpad user yet,
                create one below: in their firm, or in the holding firm when they have no firm of their own.
              </p>
              <template v-if="request.candidates && request.candidates.length">
                <h6 class="mt-3">Matches for this email or username</h6>
                <div v-for="candidate of request.candidates" :key="candidate.user" class="custom-candidate" data-test="candidate">
                  <user-summary :user="candidate" />
                  <button type="button" class="btn btn-sm btn-primary mt-1" :disabled="loading.saving" @click="link(candidate.user)">
                    Link {{ candidate.username }}
                  </button>
                </div>
              </template>
              <p v-else class="text-muted">No Launchpad user matches this email or username.</p>

              <h6 class="mt-3">Find another user</h6>
              <div class="input-group input-group-sm">
                <input
                  v-model="userSearch"
                  type="search"
                  class="form-control"
                  placeholder="Username, email or name"
                  aria-label="Find a Launchpad user"
                  @keyup.enter="findUsers"
                />
                <button class="btn btn-outline-secondary" type="button" :disabled="searching" @click="findUsers">
                  <FontAwesomeIcon v-if="searching" icon="fa-solid fa-spinner" spin class="me-1" />{{ searching ? 'Searching' : 'Search' }}
                </button>
              </div>
              <div v-if="searched && !userResults.length" class="text-muted small mt-1">No users found.</div>
              <!-- bounded, so a broad search (up to 25 users) doesn't push the decision panel off the page -->
              <div v-if="userResults.length" class="custom-search-results mt-2" data-test="user-search-results">
                <div v-for="found of userResults" :key="found.user" class="custom-candidate">
                  <user-summary :user="found" />
                  <button type="button" class="btn btn-sm btn-primary mt-1" :disabled="loading.saving" @click="link(found.user)">
                    Link {{ found.username }}
                  </button>
                </div>
              </div>
              <div v-if="userResults.length >= 25" class="text-muted small mt-1">Showing the first 25. Narrow the search to find someone else.</div>

              <!-- No Launchpad user yet: the reviewer creates one here (ADO #680), no Super Admin needed. -->
              <div v-if="canCreateUser" class="custom-create-user mt-3" data-test="create-user">
                <h6 class="mb-1">No Launchpad user yet? Create one</h6>
                <p class="text-muted small mb-2">
                  Username <b>{{ request.email }}</b
                  >, name {{ request.firstName }} {{ request.lastName }}. They get an email to set their password. Access stays off until you approve.
                </p>
                <div class="form-check">
                  <input id="create-in-firm" v-model="createMode" class="form-check-input" type="radio" value="firm" />
                  <label class="form-check-label" for="create-in-firm">In their firm</label>
                </div>
                <div v-if="createMode === 'firm'" class="ms-4 mb-2">
                  <div class="input-group input-group-sm">
                    <input
                      v-model="firmSearch"
                      type="search"
                      class="form-control"
                      placeholder="Firm name or NetSuite id"
                      aria-label="Find a firm"
                      @keyup.enter="findFirms"
                    />
                    <button class="btn btn-outline-secondary" type="button" :disabled="firmSearching" @click="findFirms">
                      <FontAwesomeIcon v-if="firmSearching" icon="fa-solid fa-spinner" spin class="me-1" />{{
                        firmSearching ? 'Searching' : 'Search'
                      }}
                    </button>
                  </div>
                  <div v-if="firmSearched && !firmResults.length" class="text-muted small mt-1">No firms found.</div>
                  <div v-if="firmResults.length" class="custom-search-results mt-2" data-test="firm-search-results">
                    <div
                      v-for="firm of firmResults"
                      :key="firm.firm"
                      class="custom-candidate custom-firm"
                      :class="{ 'custom-firm-chosen': chosenFirm && chosenFirm.firm === firm.firm }"
                      @click="chosenFirm = firm"
                    >
                      <div>
                        <b>{{ firm.firmName }}</b>
                        <span class="text-muted"
                          >(firm {{ firm.firm }}<span v-if="firm.firmType">, {{ firm.firmType }}</span
                          >)</span
                        >
                        <span v-if="firm.netsuiteId" class="text-muted"> &middot; NetSuite {{ firm.netsuiteId }}</span>
                      </div>
                      <div v-if="firm.corporateFirmName && firm.corporateFirmName !== firm.firmName" class="small">
                        Corporate: {{ firm.corporateFirmName }}
                      </div>
                      <div v-if="firm.adasApp" class="small">
                        ADAS Map: {{ firm.adasApp }} &middot; {{ firm.userCount }} active user{{ firm.userCount === 1 ? '' : 's' }}
                      </div>
                      <div v-else class="small text-danger">No ADAS Map app on this firm, so app sign-in would fail.</div>
                    </div>
                  </div>
                  <div v-if="firmResults.length >= 25" class="text-muted small mt-1">Showing the first 25. Narrow the search.</div>
                </div>
                <div class="form-check">
                  <input id="create-in-holding" v-model="createMode" class="form-check-input" type="radio" value="holding" :disabled="!holdingFirm" />
                  <label class="form-check-label" for="create-in-holding">
                    In the holding firm<span v-if="holdingFirm"> ({{ holdingFirm.firmName }})</span>
                    <span class="text-muted">: for users with no firm of their own</span>
                  </label>
                  <div v-if="holdingFirmLoaded && !holdingFirm" class="text-muted small">No holding firm is set up on this tier yet.</div>
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-primary mt-2"
                  :disabled="loading.saving || (createMode === 'firm' ? !chosenFirm || !chosenFirm.adasApp : !holdingFirm)"
                  data-test="create-user-button"
                  @click="createLaunchpadUser"
                >
                  Create user and link
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Billing and decision -->
      <div class="col-lg-8">
        <div class="card">
          <div class="card-header">Billing and decision</div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-5">
                <label for="netsuiteReference" class="form-label">NetSuite reference</label>
                <input
                  id="netsuiteReference"
                  v-model.trim="form.netsuiteReference"
                  type="text"
                  maxlength="100"
                  class="form-control"
                  placeholder="Customer, subscription or sales order"
                  :disabled="isApproved"
                />
                <div class="form-text">Required to approve.</div>
              </div>
              <div class="col-md-7">
                <label for="reviewNote" class="form-label">Internal note</label>
                <textarea
                  id="reviewNote"
                  v-model="form.reviewNote"
                  rows="2"
                  maxlength="2000"
                  class="form-control"
                  placeholder="Not shown to the requester"
                ></textarea>
              </div>
            </div>
            <div class="mt-2">
              <button type="button" class="btn btn-sm btn-outline-primary" :disabled="loading.saving || !isDirty" @click="saveDetails">
                Save details
              </button>
            </div>

            <hr />
            <div v-if="pendingAction" class="custom-reason mb-3">
              <label for="decisionReason" class="form-label"
                >Reason (emailed to the requester{{ pendingAction.to === 'Revoked' ? ' only if you tell them' : '' }}, optional)</label
              >
              <textarea id="decisionReason" v-model="form.decisionReason" rows="2" maxlength="1000" class="form-control"></textarea>
              <div class="mt-2">
                <button type="button" class="btn btn-sm btn-danger" :disabled="loading.saving" @click="move(pendingAction.to)">
                  Confirm: {{ pendingAction.label }}
                </button>
                <button type="button" class="btn btn-sm btn-light ms-2" @click="pendingAction = null">Cancel</button>
              </div>
            </div>
            <div v-else class="d-flex flex-wrap gap-2 align-items-center">
              <button
                v-for="action of actions"
                :key="action.to"
                type="button"
                class="btn btn-sm"
                :class="action.class"
                :disabled="loading.saving"
                @click="action.needsReason ? (pendingAction = action) : move(action.to)"
              >
                {{ action.label }}
              </button>
              <div v-if="emailsRequester" class="form-check ms-2">
                <input id="notifyRequester" v-model="notifyRequester" class="form-check-input" type="checkbox" />
                <label class="form-check-label" for="notifyRequester">Email the requester when approved or denied</label>
              </div>
            </div>
            <p v-if="isApproved" class="text-muted small mt-2 mb-0">Access is on. The app and ADAS IQ pick up a change within about five minutes.</p>
          </div>
        </div>
      </div>

      <!-- History -->
      <div class="col-lg-4">
        <div class="card">
          <div class="card-header">History</div>
          <ul class="list-group list-group-flush">
            <li v-for="(event, index) of request.events" :key="index" class="list-group-item small">
              <div>
                <b>{{ describe(event) }}</b>
                <span class="text-muted ms-1">{{ formatDate(event.createdOn) }}</span>
              </div>
              <div v-if="event.actor" class="text-muted">by {{ event.actor }}</div>
              <div v-if="event.note" class="custom-pre">{{ event.note }}</div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { accessRequestStore } from '@/stores/accessRequest'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import moment from 'moment'
import { AccessRequestStatus, getAccessRequestStatus, actionFor, OPEN_STATUSES } from '@/plugins/access-request-status'
import UserSummary from '@/components/access-requests/UserSummary.vue'

export default {
  name: 'AccessRequestDetail',
  components: {
    FontAwesomeIcon,
    UserSummary
  },
  props: {
    requestId: { type: Number, required: true }
  },
  emits: ['closing'],
  data() {
    return {
      form: { netsuiteReference: '', reviewNote: '', decisionReason: '' },
      notifyRequester: true,
      pendingAction: null,
      userSearch: '',
      userResults: [],
      searched: false,
      searching: false,
      // creating the requester's Launchpad user: 'firm' (pick one) or 'holding' (the tier's holding firm)
      createMode: 'firm',
      firmSearch: '',
      firmResults: [],
      firmSearched: false,
      firmSearching: false,
      chosenFirm: null,
      holdingFirm: null,
      holdingFirmLoaded: false,
      error: null,
      notice: null
    }
  },
  computed: {
    ...mapState(accessRequestStore, ['request', 'loading']),
    status() {
      return getAccessRequestStatus(this.request?.status)
    },
    // a user can be created while the request is still open and unlinked
    canCreateUser() {
      return !!this.request && !this.request.linkedUser && OPEN_STATUSES.includes(this.request.status)
    },
    isApproved() {
      return this.request?.status === AccessRequestStatus.Approved
    },
    canEditLink() {
      return !this.isApproved
    },
    actions() {
      const from = this.request?.status
      return (this.request?.nextStatuses || []).map((to) => ({ to, ...actionFor(from, to) }))
    },
    emailsRequester() {
      return (this.request?.nextStatuses || []).some((s) => [AccessRequestStatus.Approved, AccessRequestStatus.Denied].includes(s))
    },
    isDirty() {
      if (!this.request) return false
      return (
        (this.form.netsuiteReference || '') !== (this.request.netsuiteReference || '') ||
        (this.form.reviewNote || '') !== (this.request.reviewNote || '')
      )
    }
  },
  watch: {
    requestId: {
      immediate: true,
      handler() {
        this.load()
      }
    }
  },
  unmounted() {
    this.clearRequest()
  },
  methods: {
    ...mapActions(accessRequestStore, ['getRequest', 'clearRequest', 'searchUsers', 'updateRequest', 'searchFirms', 'getHoldingFirm', 'createUser']),
    async load() {
      this.error = null
      this.notice = null
      try {
        await this.getRequest(this.requestId)
        this.resetForm()
      } catch (error) {
        this.error = `Could not load the request: ${error}`
      }
      if (!this.holdingFirmLoaded) {
        try {
          this.holdingFirm = await this.getHoldingFirm()
        } catch (error) {
          this.holdingFirm = null
        }
        this.holdingFirmLoaded = true
      }
    },
    async findFirms() {
      this.error = null
      this.chosenFirm = null
      if ((this.firmSearch || '').trim().length < 2) {
        this.firmResults = []
        this.firmSearched = false
        return
      }
      this.firmSearching = true
      try {
        this.firmResults = await this.searchFirms(this.firmSearch.trim())
        this.firmSearched = true
      } catch (error) {
        this.error = `Firm search failed: ${error}`
      } finally {
        this.firmSearching = false
      }
    },
    // Creates the requester's Launchpad user (in the chosen firm, or the holding firm) and links it.
    // The API pushes it to the firm's ADAS Map instance and sends the set-password email; the
    // response says whether each of those worked, so a failure is shown, never hidden.
    async createLaunchpadUser() {
      this.error = null
      this.notice = null
      const typed = { netsuiteReference: this.form.netsuiteReference, reviewNote: this.form.reviewNote }
      try {
        const result = await this.createUser({
          id: this.request.id,
          expectedStatus: this.request.status,
          expectedLastUpdatedOn: this.request.lastUpdatedOn,
          firm: this.createMode === 'firm' ? this.chosenFirm.firm : null,
          useHoldingFirm: this.createMode === 'holding'
        })
        this.resetForm()
        Object.assign(this.form, typed)
        this.firmResults = []
        this.firmSearched = false
        this.chosenFirm = null
        const created = result && result.createdUser
        let text = created ? `Created Launchpad user ${created.username} in ${created.firmName} and linked it.` : 'User created and linked.'
        if (created && !created.pushed) {
          text += ' The push to ADAS Map FAILED, so app sign-in will not work until a Super Admin pushes the user from the user dialog.'
        } else if (created && created.passwordEmailed === true) {
          text += ' They were emailed a link to set their password.'
        } else if (created && created.passwordEmailed === false) {
          text += ' The set-password email could not be sent; they can use Forgot password in the app.'
        }
        this.notice = text
      } catch (error) {
        this.error = error
        if (String(error).includes('Someone else')) {
          await this.getRequest(this.requestId)
        }
      }
    },
    resetForm() {
      this.form = {
        netsuiteReference: this.request?.netsuiteReference || '',
        reviewNote: this.request?.reviewNote || '',
        decisionReason: this.request?.decisionReason || ''
      }
      this.pendingAction = null
    },
    // keepForm: a link change sends only the user, so keep any NetSuite reference or note
    // the reviewer has typed but not saved yet.
    async save(changes, successText, { keepForm = false } = {}) {
      this.error = null
      this.notice = null
      const typed = { netsuiteReference: this.form.netsuiteReference, reviewNote: this.form.reviewNote }
      try {
        // expectedLastUpdatedOn: the version on screen. Someone else's edit since then is a
        // 409, so nobody approves a link or reference they haven't seen.
        const result = await this.updateRequest({
          id: this.request.id,
          expectedStatus: this.request.status,
          expectedLastUpdatedOn: this.request.lastUpdatedOn,
          ...changes
        })
        this.resetForm()
        if (keepForm) Object.assign(this.form, typed)
        this.userResults = []
        this.searched = false
        let text = successText
        if (result && result.requesterEmailed === true) text += ' The requester was emailed.'
        if (result && result.requesterEmailed === false) text += ' The requester email could not be sent.'
        this.notice = text
      } catch (error) {
        this.error = error
        // a 409 means someone else moved it; show them what it is now
        if (String(error).includes('Someone else')) {
          await this.getRequest(this.requestId)
        }
      }
    },
    link(user) {
      this.save({ launchpadUser: user }, user ? 'User linked.' : 'User unlinked.', { keepForm: true })
    },
    saveDetails() {
      this.save({ netsuiteReference: this.form.netsuiteReference, reviewNote: this.form.reviewNote }, 'Saved.')
    },
    move(to) {
      const changes = {
        status: to,
        netsuiteReference: this.form.netsuiteReference,
        reviewNote: this.form.reviewNote,
        notifyRequester: this.notifyRequester
      }
      if (to === AccessRequestStatus.Denied || to === AccessRequestStatus.Revoked) {
        changes.decisionReason = this.form.decisionReason
      }
      this.save(changes, `Moved to ${getAccessRequestStatus(to).name}.`)
    },
    async findUsers() {
      this.error = null
      if ((this.userSearch || '').trim().length < 2) {
        this.userResults = []
        this.searched = false
        return
      }
      this.searching = true
      try {
        this.userResults = await this.searchUsers(this.userSearch.trim())
        this.searched = true
      } catch (error) {
        this.error = `User search failed: ${error}`
      } finally {
        this.searching = false
      }
    },
    describe(event) {
      switch (event.action) {
        case 'Submitted':
          return 'Submitted from the app'
        case 'Resubmitted':
          return 'Sent again from the app'
        case 'StatusChanged':
          return `${getAccessRequestStatus(event.fromStatus).name} → ${getAccessRequestStatus(event.toStatus).name}`
        default:
          return 'Details updated'
      }
    },
    formatDate(value) {
      return value ? moment(value).format('MM/DD/YYYY h:mm A') : ''
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-spinner {
  text-align: center;
  padding: 2rem;
  font-size: 1.5rem;
}
.custom-fields dt {
  font-weight: 600;
  font-size: 0.85rem;
  color: #6c757d;
}
.custom-fields dd {
  margin-bottom: 0.6rem;
}
.custom-pre {
  white-space: pre-wrap;
}
.custom-search-results {
  max-height: 22rem;
  overflow-y: auto;
  padding-right: 0.25rem;
}
.custom-search-results .custom-candidate:first-child {
  margin-top: 0;
}
.custom-candidate {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 0.5rem 0.75rem;
  margin-top: 0.5rem;
}
.custom-create-user {
  border-top: 1px solid #dee2e6;
  padding-top: 0.75rem;
}
.custom-firm {
  cursor: pointer;
}
.custom-firm-chosen {
  border-color: #0d6efd;
  background: #eef4ff;
}
</style>
